export const formatPriceToShorthandPrice = (value: number): string => {
  if (value >= 1_000_000) {
    return `$${value / 1_000_000}m`;
  }
  if (value > 1_000) {
    return `$${value / 1_000}k`;
  }
  return `$${value}`;
};

export const roundPriceToNearestThousand = (price: number): number => {
  return Math.round(price / 1000) * 1000;
};

export const convertPriceToShortForm = ({
  money,
  digitsInBillions = 2,
  digitsInMillions = 2,
  digitsInThousands = 0,
}: {
  money: number;
  digitsInBillions?: number;
  digitsInMillions?: number;
  digitsInThousands?: number;
}): string => {
  const CURRENCY_SYMBOL = {
    AUD: '$',
  };

  const billion = 1_000_000_000;
  const million = 1_000_000;
  const thousand = 1000;

  if (money >= 1000) {
    // Round up to nearest thousand to prevent numbers like 999,999 from showing as 1000k
    const moneyToNearestThousand = roundPriceToNearestThousand(money);

    if (moneyToNearestThousand >= billion) {
      const mult = 10 ** digitsInBillions;
      const moneyInBillions = Math.round((money * mult) / billion) / mult;
      return `${CURRENCY_SYMBOL.AUD}${moneyInBillions}b`;
    }
    if (moneyToNearestThousand >= million) {
      const mult = 10 ** digitsInMillions;
      const moneyInMillions = Math.round((money * mult) / million) / mult;
      return `${CURRENCY_SYMBOL.AUD}${moneyInMillions}m`;
    }
    if (moneyToNearestThousand >= thousand) {
      const mult = 10 ** digitsInThousands;
      const moneyInThousand = Math.round((money * mult) / thousand) / mult;
      return `${CURRENCY_SYMBOL.AUD}${moneyInThousand}k`;
    }
  }

  return auCurrencyFormatter.format(money);
};

const auCurrencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});

interface Options {
  shortForm?: boolean;
  roundToNearestThousand?: boolean;
}

export const formatPrice = ({
  price,
  options = { shortForm: false, roundToNearestThousand: false },
}: {
  price: number;
  options?: Options;
}): string => {
  let adjustedPrice = price;

  // Prevent rounding to $0
  if (price < 0.5 && price > 0) {
    adjustedPrice = 1;
  }

  if (!options.shortForm) {
    adjustedPrice = options.roundToNearestThousand
      ? roundPriceToNearestThousand(adjustedPrice)
      : adjustedPrice;
    return auCurrencyFormatter.format(adjustedPrice);
  }

  return convertPriceToShortForm({ money: adjustedPrice });
};
